import { useEffect, useRef } from 'react';
import { navigationState } from 'utils/navigationControl';

interface NavigationTrapProps {
  onBack: () => void;
  onRestore: (event: PageTransitionEvent) => void;
  isEnabled?: boolean;
}

export const useRedirectBack = ({ onBack, onRestore, isEnabled = true }: NavigationTrapProps) => {
  // Use refs for callbacks to avoid re-running useEffect on their changes
  const savedBack = useRef(onBack);
  const savedRestore = useRef(onRestore);

  // Keep refs up to date on every render
  useEffect(() => {
    savedBack.current = onBack;
    savedRestore.current = onRestore;
  }, [onBack, onRestore]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    if (isEnabled) {
      const handlePopState = (): void => {
        if (navigationState.isInternal) {
          navigationState.isInternal = false;
          return;
        }

        // Call the latest callback from ref
        savedBack.current();

        // After onBack, we re-establish the trap to prevent further "back" clicks
        // using the same clean URL strategy.
        if (window.history.state?.trap !== true) {
          window.history.pushState(
            {
              trap: true,
            },
            '',
            window.location.pathname + window.location.search,
          );
        }
      };

      const handlePageShow = (event: PageTransitionEvent): void => {
        savedRestore.current(event);
      };

      /**
       * 1. Uses a hash (#init) to force the browser to recognize a new history entry.
       * 2. Uses replaceState + pushState to create a clear transition for the popstate event.
       * 3. Re-arms on gesture as a fallback for strict browser policies.
       */
      const initTrap = () => {
        if (window.history.state?.trap !== true) {
          // Skip trap if current history entry has React Router state with background
          // (modal routes like /content). Pushing a trap here would destroy the
          // background state needed for proper back-navigation.
          const currentState = window.history.state;
          if (currentState?.usr?.background) {
            return;
          }

          const baseUrl = window.location.pathname;
          const searchParams = new URLSearchParams(window.location.search);
          const cleanUrl = baseUrl + window.location.search;

          window.history.replaceState(
            {
              trap: false,
            },
            '',
            cleanUrl,
          );

          searchParams.set('nativeTrap', 'active');

          const trapUrl = `${baseUrl}?${searchParams.toString()}`;

          window.history.pushState(
            {
              trap: true,
            },
            '',
            trapUrl,
          );
        }
      };

      // Immediate attempt
      initTrap();

      // Backup activation on first interaction
      const gestureListener = () => {
        initTrap();
        window.removeEventListener('mousedown', gestureListener);
        window.removeEventListener('touchstart', gestureListener);
      };

      window.addEventListener('mousedown', gestureListener);
      window.addEventListener('touchstart', gestureListener);
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('pageshow', handlePageShow);

      cleanup = () => {
        window.removeEventListener('mousedown', gestureListener);
        window.removeEventListener('touchstart', gestureListener);
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('pageshow', handlePageShow);
      };
    }

    return cleanup;
  }, [isEnabled]); // Depends only on the enabled flag
};
